import { useCallback } from "react";

export function useParseGradient() {
  const hexToRgb = useCallback((hex: string): string => {
    hex = hex.replace("#", "");
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    if (hex.length === 8) {
      const a = parseInt(hex.substring(6, 8), 16) / 255;
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
  }, []);

  const parseGradient = useCallback(
    (gradientString: string) => {
      const defaultResult = {
        gradientType: "linear",
        gradientAngle: 45,
        stops: [
          { color: "rgb(215, 225, 236)", opacity: 1, position: 0 },
          { color: "rgb(255, 255, 255)", opacity: 1, position: 100 },
        ],
      };

      if (typeof gradientString !== "string" || !gradientString.trim()) {
        console.warn("Invalid gradientString, using default values.");
        return defaultResult;
      }

      try {
        let gradientType = "linear";
        if (gradientString.includes("radial-gradient")) {
          gradientType = "radial";
        } else if (gradientString.includes("conic-gradient")) {
          gradientType = "conic";
        }

        let angle = 45;
        let stops: { color: string; opacity: number; position: number }[] = [];

        if (gradientType === "linear") {
          const angleMatch = gradientString.match(
            /linear-gradient\(\s*(\d+)deg/,
          );
          if (angleMatch && angleMatch[1]) {
            angle = parseInt(angleMatch[1], 10);
          }
        }

        const colorStopRegex =
          /(rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*[\d.]+\s*)?\)|#[0-9a-fA-F]{3,8})\s*(\d+%)?/gi;

        let match;
        let hasStops = false;

        while ((match = colorStopRegex.exec(gradientString)) !== null) {
          hasStops = true;
          const [_, colorStr, positionStr] = match;

          let position = 0;
          if (stops.length === 0) {
            position = 0;
          } else if (positionStr) {
            position = parseInt(positionStr, 10);
          } else {
            const lastPos = stops[stops.length - 1].position;
            position = lastPos + (100 - lastPos) / 2;
          }

          let color: string,
            opacity = 1;

          if (colorStr.startsWith("#")) {
            color = hexToRgb(colorStr);
          } else if (colorStr.startsWith("rgba")) {
            const rgbaMatch = colorStr.match(
              /rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/,
            );
            if (rgbaMatch) {
              const [_, r, g, b, a] = rgbaMatch;
              color = `rgb(${r}, ${g}, ${b})`;
              opacity = parseFloat(a);
            } else {
              color = colorStr;
            }
          } else {
            color = colorStr;
          }

          stops.push({ color, opacity, position });
        }

        if (!hasStops) {
          console.warn(
            "No valid color stops found in gradient string, using defaults.",
          );
          return defaultResult;
        }

        if (stops.length === 1) {
          stops.push({ ...stops[0], position: 100 });
        }

        if (stops.length === 2 && gradientType === "linear") {
          return {
            gradientType,
            gradientAngle: angle - 90,
            startColor: stops[0].color,
            startOpacity: stops[0].opacity,
            endColor: stops[1].color,
            endOpacity: stops[1].opacity,
            stops,
          };
        }

        return {
          gradientType,
          gradientAngle: angle - 90,
          stops,
        };
      } catch (error) {
        console.error("Error parsing gradient:", error);
        return defaultResult;
      }
    },
    [hexToRgb],
  );

  return { parseGradient };
}
